import React, { useState, useEffect, useRef } from 'react';
import { X, Camera, Plus, Trash2, ChevronDown } from 'lucide-react';
import BarcodeScannerModal from './BarcodeScannerModal';
import { useToast } from '../../hooks/use-toast';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";

const StockOut = ({ axiosInstance }) => {
  // --- State ---
  const { toast } = useToast();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]); // Added brands state
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null); // Selected Warehouse
  const [formData, setFormData] = useState({
    siNumber: '',
    drNumber: '',
    customerName: '',
    orderDate: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  
  // Item Search State & Ref
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [itemSearchResults, setItemSearchResults] = useState([]);
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const itemSearchRef = useRef(null);
  const [selectedItemForFrom, setSelectedItemForFrom] = useState(null);
  const [fromWarehouseId, setFromWarehouseId] = useState(null);
  const [fromWarehouseAvailableQty, setFromWarehouseAvailableQty] = useState(null);
  const [lastFromSelection, setLastFromSelection] = useState(null);

  const [openSerialDropdown, setOpenSerialDropdown] = useState(null); // Row ID of open serial dropdown

  const [brandFilter, setBrandFilter] = useState(''); // New Brand Filter
  const [rows, setRows] = useState([]); // Selected Items (Cart)
  const [isLoading, setIsLoading] = useState(true);

  // Scanner State
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [activeScanRow, setActiveScanRow] = useState(null);
  const [scanField, setScanField] = useState(null); // 'product' or 'serial'
  
  // Search Modals State
  const [productSelectionModal, setProductSelectionModal] = useState({ isOpen: false, items: [], rowId: null });
  const [notFoundDialog, setNotFoundDialog] = useState({ isOpen: false, message: '' });
  const [isSubmitConfirmOpen, setIsSubmitConfirmOpen] = useState(false);

  // --- Outside Click Listener for Item Search ---
  useEffect(() => {
    const handleClickOutside = (event) => {
        if (itemSearchRef.current && !itemSearchRef.current.contains(event.target)) {
            setShowItemDropdown(false);
        }
        if (!event.target.closest('.serial-dropdown-container')) {
            setOpenSerialDropdown(null);
        }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- Data Fetching ---
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch Warehouses
        let mainWarehouseId = null;
        try {
            const warehouseRes = await axiosInstance.get('/inventory/warehouses');
            setWarehouses(warehouseRes.data);
            const main = warehouseRes.data.find(w => w.name === 'Main Warehouse');
            mainWarehouseId = main ? main.id : (warehouseRes.data[0] ? warehouseRes.data[0].id : null);
            setSelectedWarehouse(mainWarehouseId);
        } catch (err) {
            console.error("Error fetching warehouses:", err);
        }

        // Fetch Customers
        let customerList = [];
        try {
          const projectsRes = await axiosInstance.get('/ccp/project-details');
          if (Array.isArray(projectsRes.data)) {
            const uniqueCustomers = [...new Set(projectsRes.data
              .map(p => p.end_user || p.account_name || p.client_name)
              .filter(name => name && name.trim() !== '')
            )];
            customerList = uniqueCustomers.sort();
          }
        } catch (err) {
          console.error("Error fetching customers:", err);
        }
        setCustomers(customerList);

        // Fetch Products
        const endpoints = [
          { url: '/stock/machines', type: 'Machine' },
          { url: '/stock/consumables', type: 'Consumable' },
          { url: '/stock/spare-parts', type: 'Spare Part' },
          { url: '/stock/demo-units', type: 'Demo Unit' },
          { url: '/stock/accessories', type: 'Accessory' }
        ];

        const productPromises = endpoints.map(ep => 
          axiosInstance.get(ep.url).then(res => ({ data: res.data, type: ep.type })).catch(err => ({ data: [], type: ep.type, error: err }))
        );

        const results = await Promise.all(productPromises);
        
        let allProducts = [];
        let allBrands = new Set(); // Collect unique brands

        results.forEach(res => {
          if (Array.isArray(res.data)) {
            const normalized = res.data.map(item => {
              if (item.brand) allBrands.add(item.brand);
              return {
                id: item._id || item.id,
                lot: res.type,
                brand: item.brand || '',
                model: item.model || '',
                description: item.itemDescription || item.item_description || item.description || '',
                serialNumber: item.serialNumber || item.serial_number || '',
                itemCode: item.itemCode || item.item_code || '',
                partNumber: item.partNumber || item.part_number || '', // Assuming part number exists
                quantity: item.quantity || item.stock_quantity || 0,
                price: item.price || item.unit_price || 0,
                warehouseId: item.warehouse_id || mainWarehouseId, // Use fetched ID or fallback
                originalData: item
              };
            });
            allProducts = [...allProducts, ...normalized];
          }
        });
        
        setProducts(allProducts);
        setBrands([...allBrands].sort()); // Set sorted brands

      } catch (error) {
        console.error("Error loading initial data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [axiosInstance]);

  // --- Handlers ---

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRemoveRow = (rowId) => {
    setRows(prev => prev.filter(row => row.id !== rowId));
  };

  // --- Filter Logic for Search ---
  // Filters the products available for search/scan.
  const searchableProducts = products.filter(product => {
      // 1. Warehouse Filter
      if (selectedWarehouse && product.warehouseId !== selectedWarehouse) return false;

      return true; 
  });

  const normalize = (value) => (value || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');

  const isSameGroupedItem = (p, selected) => {
      if (!selected) return false;
      if (p.lot !== selected.lot) return false;
      if (selected.lot === 'Accessory') {
          return (
              normalize(p.brand) === normalize(selected.brand) &&
              normalize(p.model) === normalize(selected.model) &&
              normalize(p.description) === normalize(selected.description)
          );
      }
      return (
          (selected.itemCode && p.itemCode && p.itemCode === selected.itemCode) ||
          (selected.partNumber && p.partNumber && p.partNumber === selected.partNumber) ||
          (selected.itemCode && p.partNumber && p.partNumber === selected.itemCode) ||
          (selected.partNumber && p.itemCode && p.itemCode === selected.partNumber)
      );
  };

  const getWarehousesWithStockForItem = (selected) => {
      if (!selected) return [];

      const qtyByWarehouse = new Map();

      products.forEach(p => {
          if (!isSameGroupedItem(p, selected)) return;
          const qty = parseInt(p.quantity, 10) || 0;
          if (qty <= 0) return;
          const key = p.warehouseId === null || p.warehouseId === undefined ? '' : String(p.warehouseId);
          qtyByWarehouse.set(key, (qtyByWarehouse.get(key) || 0) + qty);
      });

      const options = [];
      qtyByWarehouse.forEach((qty, warehouseId) => {
          const w = warehouses.find(x => String(x.id) === String(warehouseId));
          if (!w) return;
          options.push({ warehouseId, name: w.name, qty });
      });

      options.sort((a, b) => a.name.localeCompare(b.name));
      return options;
  };

  // --- Search Logic (Manual & Scan) ---
  const handleItemSearchChange = (e) => {
      const query = e.target.value;
      setItemSearchQuery(query);

      if (!query.trim()) {
          setItemSearchResults([]);
          setShowItemDropdown(false);
          return;
      }

      const searchTerm = query.toLowerCase();
      
      const matches = [];
      const seen = new Set();

      products.forEach(p => {
          const model = (p.model || '').toLowerCase();
          const desc = (p.description || '').toLowerCase();
          
          if (model.includes(searchTerm) || desc.includes(searchTerm)) {
              // Group by lot, itemCode and model
              const key = p.lot === 'Accessory'
                ? `${p.lot}-${normalize(p.brand)}-${normalize(p.model)}-${normalize(p.description)}`
                : `${p.lot}-${p.itemCode || p.partNumber}-${p.model}`;
              if (!seen.has(key)) {
                  seen.add(key);
                  matches.push(p);
              }
          }
      });

      setItemSearchResults(matches);
      setShowItemDropdown(true);
  };

  const handleSelectItemFromDropdown = (product) => {
      setSelectedItemForFrom(product);
      setFromWarehouseId(null);
      setFromWarehouseAvailableQty(null);
      setItemSearchQuery('');
      setItemSearchResults([]);
      setShowItemDropdown(false);
  };

  const handleSelectFromWarehouse = (warehouseId) => {
      if (!selectedItemForFrom) return;

      setFromWarehouseId(warehouseId);
      setSelectedWarehouse(warehouseId);

      const matchingItems = products.filter(p => (
          String(p.warehouseId) === String(warehouseId) &&
          isSameGroupedItem(p, selectedItemForFrom)
      ));

      const totalQty = matchingItems.reduce((sum, p) => sum + (parseInt(p.quantity, 10) || 0), 0);
      setFromWarehouseAvailableQty(totalQty);

      const representativeProduct = matchingItems[0] || selectedItemForFrom;
      updateRowWithProduct(null, representativeProduct, totalQty, false);

      const warehouseName = warehouses.find(w => String(w.id) === String(warehouseId))?.name || '';
      setLastFromSelection({
          warehouseId,
          warehouseName,
          availableQty: totalQty,
          model: selectedItemForFrom.model || '',
          description: selectedItemForFrom.description || ''
      });

      setSelectedItemForFrom(null);
      setFromWarehouseId(null);
      setFromWarehouseAvailableQty(null);
  };

  const handleGlobalSearchSubmit = () => {
      const searchTerm = searchQuery.trim().toLowerCase();
      if (!searchTerm) return;
      
      // Use searchableProducts to respect the Type filter
      const strictMatches = searchableProducts.filter(p => {
          const itemCode = (p.itemCode || '').toString().trim().toLowerCase();
          const partNumber = (p.partNumber || '').toString().trim().toLowerCase();
          // Removed serial search from here
          return itemCode === searchTerm || partNumber === searchTerm;
      });

      if (strictMatches.length === 1) {
           addItemToOrder(strictMatches[0], null, true);
           setSearchQuery(''); 
      } else if (strictMatches.length > 1) {
           setProductSelectionModal({ isOpen: true, items: strictMatches, rowId: null });
      } else {
           // No match found in the filtered list
           setNotFoundDialog({ isOpen: true, message: "No Item Found" });
      }
  };

  const addItemToOrder = (product, explicitAvailableQty = null, fillSerial = false) => {
      // Determine Item Code or Part Number based on Lot Type
      let itemCode = '';
      let partNumber = '';

      if (product.lot === 'Spare Part') {
          // For Spare Parts, use itemCode field as Part Number (since backend stores it in itemCode)
          // OR if backend had a partNumber field, we'd use that.
          // Based on server.py check: StockSparePart has 'itemCode'. 
          // User says "Spare Part uses Part Number". 
          // So we map product.itemCode to the row's partNumber field for display/logic.
          partNumber = product.itemCode || '';
          itemCode = ''; // Clear itemCode for Spare Parts
      } else {
          // For others, use itemCode
          itemCode = product.itemCode || '';
          partNumber = '';
      }

      setRows(prev => [
          ...prev,
          {
              id: Date.now(),
              lot: product.lot,
              productId: product.id,
              brand: product.brand,
              model: product.model,
              description: product.description,
              serialNumber: fillSerial ? (product.serialNumber || '') : '', 
              availableQty: explicitAvailableQty !== null ? explicitAvailableQty : product.quantity,
              orderQuantity: '',
              price: product.price,
              itemCode: itemCode,
              partNumber: partNumber
          }
      ]);
  };

  const updateRowWithProduct = (rowId, product, explicitAvailableQty = null, fillSerial = false) => {
      if (rowId === null) {
          addItemToOrder(product, explicitAvailableQty, fillSerial);
          return;
      }
      
      // Determine Item Code or Part Number based on Lot Type (Same logic as addItemToOrder)
      let itemCode = '';
      let partNumber = '';

      if (product.lot === 'Spare Part') {
          partNumber = product.itemCode || '';
          itemCode = '';
      } else {
          itemCode = product.itemCode || '';
          partNumber = '';
      }

      setRows(prev => prev.map(r => {
          if (r.id === rowId) {
              return {
                  ...r,
                  lot: product.lot,
                  productId: product.id,
                  brand: product.brand,
                  model: product.model,
                  description: product.description,
                  serialNumber: fillSerial ? (product.serialNumber || '') : '', 
                  availableQty: explicitAvailableQty !== null ? explicitAvailableQty : product.quantity,
                  price: product.price,
                  orderQuantity: '', 
                  itemCode: itemCode,
                  partNumber: partNumber
              };
          }
          return r;
      }));
  };
  
  const handleSerialSubmit = (rowId, serialValue) => {
      if (!serialValue.trim()) return;
      
      // Strict search: Serial Number only
      const product = products.find(p => p.serialNumber === serialValue.trim());
      
      if (!product) {
           setNotFoundDialog({ isOpen: true, message: "No Item Found" });
      } else {
          // Specific item found by serial -> Available Qty is 1 (or its specific qty)
          // And we DO fill the serial (obviously, user typed it)
          updateRowWithProduct(rowId, product, 1, true);
      }
  };

  const handleGlobalKeyDown = (e) => {
      if (e.key === 'Enter') {
          handleGlobalSearchSubmit();
      }
  };
  
  const handleSerialKeyDown = (e, rowId, value) => {
      if (e.key === 'Enter') {
          handleSerialSubmit(rowId, value);
      }
  };

  // --- Scanner Logic ---
  const handleOpenScanner = () => {
    // setActiveScanRow(rowId); // No rowId for global scan
    setScanField('product');
    setIsScannerOpen(true);
  };
  
  const handleSerialOpenScanner = (rowId) => {
    setActiveScanRow(rowId);
    setScanField('serial');
    setIsScannerOpen(true);
  };

  const handleScanSuccess = (decodedText) => {
    console.log("Scanned:", decodedText);
    
    if (scanField === 'product') {
        // Search for product
        const product = products.find(p => 
            (p.serialNumber && p.serialNumber === decodedText) ||
            (p.itemCode && p.itemCode === decodedText) ||
            (p.partNumber && p.partNumber === decodedText) ||
            (p.id === decodedText) // Fallback to ID
        );

        if (product) {
            addItemToOrder(product, null, true); // Add new item
            toast({
                title: "Product Found",
                description: `${product.brand} ${product.model} added.`,
            });
        } else {
            toast({
                variant: "destructive",
                title: "Not Found",
                description: "Product not found in inventory.",
            });
        }
    } else if (scanField === 'serial' && activeScanRow) {
        setRows(prev => prev.map(row => {
            if (row.id === activeScanRow) {
                return { ...row, serialNumber: decodedText };
            }
            return row;
        }));
    }
  };

  const handleOrderQtyChange = (rowId, value) => {
    // Allow numbers and commas
    const cleanValue = value.replace(/[^0-9]/g, '');
    const formattedValue = cleanValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    
    setRows(prev => prev.map(row => {
        if (row.id === rowId) {
            return { ...row, orderQuantity: formattedValue };
        }
        return row;
    }));
  };

  const handleSerialChange = (rowId, value) => {
      setRows(prev => prev.map(row => {
          if (row.id === rowId) {
              return { ...row, serialNumber: value };
          }
          return row;
      }));
  };

  const handleSubmit = async () => {
      setIsSubmitConfirmOpen(false);
      // Validate
      if (!formData.customerName || !formData.orderDate) {
          toast({ variant: "destructive", title: "Validation Error", description: "Please fill customer name and order date." });
          return;
      }
      
      const validItems = rows.filter(r => r.productId && r.orderQuantity);
      if (validItems.length === 0) {
          toast({ variant: "destructive", title: "Validation Error", description: "Please add at least one valid product." });
          return;
      }

      const payload = {
          si_number: formData.siNumber.trim() ? formData.siNumber.trim() : null,
          dr_number: formData.drNumber.trim() ? formData.drNumber.trim() : null,
          customerName: formData.customerName,
          orderDate: formData.orderDate,
          items: validItems.map(item => ({
              lot: item.lot,
              productId: item.productId,
              brand: item.brand,
              model: item.model,
              itemDescription: item.description,
              serialNumber: item.serialNumber,
              itemCode: item.itemCode || '',
              partNumber: item.partNumber || '',
              orderQuantity: parseInt(item.orderQuantity.replace(/,/g, ''), 10),
              availableQuantity: parseInt(item.availableQty, 10),
              price: parseFloat(item.price) || 0
          }))
      };

      try {
          await axiosInstance.post('/stock/out', payload);
          toast({ title: "Success", description: "Stock out transaction recorded." });
          // Reset form
          setFormData({ siNumber: '', drNumber: '', customerName: '', orderDate: '' });
          setRows([]);
      } catch (error) {
          console.error("Submission error:", error);
          toast({ variant: "destructive", title: "Error", description: error.response?.data?.detail || "Failed to submit transaction." });
      }
  };

  const fromWarehouseOptions = getWarehousesWithStockForItem(selectedItemForFrom);

  return (
    <div className="p-6 h-full flex flex-col bg-gray-50 space-y-6">
      <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">Stock Out</h2>
      </div>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-[30%_70%] gap-6 h-[calc(100vh-150px)] overflow-visible">
      {/* --- Left Panel (30%) --- */}
      <div className="bg-white p-4 rounded-lg shadow flex flex-col gap-4 overflow-visible h-full">
        
        {/* Top: Controls */}
        <div className="space-y-4 border-b pb-4">
            <h2 className="text-lg font-bold text-gray-800">Search</h2>
            
            {/* Warehouse Dropdown */}
            <div className="space-y-2">
                <Label>Warehouse</Label>
                <Select value={selectedWarehouse || ''} onValueChange={setSelectedWarehouse}>
                    <SelectTrigger>
                        <SelectValue placeholder="Select Warehouse" />
                    </SelectTrigger>
                    <SelectContent>
                        {warehouses.map(w => (
                            <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Search Input */}
            <div className="space-y-2">
                <Label>Item Code / Part Number</Label>
                <div className="flex items-center gap-2">
                    <Input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={handleGlobalKeyDown}
                        // onBlur={handleGlobalSearchSubmit} 
                        placeholder="Item Code, Part Number..."
                        className="flex-1"
                    />
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleOpenScanner()}
                        title="Scan Product"
                    >
                        <Camera className="h-4 w-4" />
                    </Button>
                </div>
                <p className="text-xs text-gray-500">Scan or type item code/part number and press Enter.</p>
            </div>

            {/* Item Search Input */}
            <div className="space-y-2" ref={itemSearchRef}>
                <Label>Item Search</Label>
                <div className="relative">
                    <Input
                        type="text"
                        value={itemSearchQuery}
                        onChange={handleItemSearchChange}
                        placeholder="Search by Model or Description..."
                        className="w-full"
                    />
                    {showItemDropdown && itemSearchResults.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {itemSearchResults.map((item, index) => (
                                <div
                                    key={`${item.id}-${index}`}
                                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm border-b last:border-b-0"
                                    onClick={() => handleSelectItemFromDropdown(item)}
                                >
                                    <div className="font-medium text-gray-800">{item.model || 'No Model'}</div>
                                    <div className="text-xs text-gray-500 truncate" title={item.description}>{item.description || 'No Description'}</div>
                                </div>
                            ))}
                        </div>
                    )}
                    {showItemDropdown && itemSearchResults.length === 0 && itemSearchQuery.trim() !== '' && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-4 text-center text-sm text-gray-500">
                            No items found.
                        </div>
                    )}
                </div>
            </div>

            <div className="space-y-2">
                <Label>From</Label>
                <Select
                    value={fromWarehouseId || ''}
                    onValueChange={handleSelectFromWarehouse}
                    disabled={!selectedItemForFrom || fromWarehouseOptions.length === 0}
                >
                    <SelectTrigger>
                        <SelectValue placeholder={selectedItemForFrom ? "Select Warehouse" : "Select item first"} />
                    </SelectTrigger>
                    <SelectContent>
                        {fromWarehouseOptions.map(w => (
                            <SelectItem key={w.warehouseId} value={w.warehouseId}>{w.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {selectedItemForFrom && fromWarehouseOptions.length === 0 && (
                    <p className="text-xs text-gray-500">No warehouses with available stock for this item.</p>
                )}
            </div>

            {lastFromSelection && (
                <div className="text-xs text-gray-600">
                    From {lastFromSelection.warehouseName}: {lastFromSelection.availableQty}
                </div>
            )}
        </div>

        {/* Middle: Transaction Info (Moved to Right Panel) */}
        {/* <div className="space-y-4">
            <h2 className="text-lg font-bold text-gray-800">Transaction Details</h2>
            <div className="space-y-2">
                <Label>Customer Name</Label>
                <Input
                    type="text"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleInputChange}
                    placeholder="Enter customer name"
                />
            </div>
            <div className="space-y-2">
                <div 
                    className="cursor-pointer"
                    onClick={() => document.getElementById('orderDateInput').showPicker()}
                >
                    <Label className="cursor-pointer">Order Date</Label>
                    <div className="relative mt-2">
                        <Input
                            id="orderDateInput"
                            type="date"
                            name="orderDate"
                            value={formData.orderDate}
                            onChange={handleInputChange}
                            className="w-full cursor-pointer"
                        />
                    </div>
                </div>
            </div>
        </div> */}
      </div>

      {/* --- Right Panel (70%) --- */}
      <div className="bg-white p-6 rounded-lg shadow flex flex-col h-full overflow-hidden">
        
        {/* Transaction Details (Moved Here) */}
        <div className="grid grid-cols-2 gap-4 mb-6 border-b pb-4">
            <div className="space-y-2">
                <Label>SI Number</Label>
                <Input
                    type="text"
                    name="siNumber"
                    value={formData.siNumber}
                    onChange={handleInputChange}
                    placeholder="Enter SI number"
                />
            </div>
            <div className="space-y-2">
                <Label>DR Number</Label>
                <Input
                    type="text"
                    name="drNumber"
                    value={formData.drNumber}
                    onChange={handleInputChange}
                    placeholder="Enter DR number"
                />
            </div>
            <div className="space-y-2">
                <Label>Customer Name</Label>
                <Input
                    type="text"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleInputChange}
                    placeholder="Enter customer name"
                />
            </div>
            <div className="space-y-2">
                <Label>Order Date</Label>
                <Input
                    type="date"
                    name="orderDate"
                    value={formData.orderDate}
                    onChange={handleInputChange}
                />
            </div>
        </div>

        <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">Order List</h2>
            <div className="text-sm text-gray-500">
                {rows.length} items selected
            </div>
        </div>

        <div className="border rounded-md flex-1 overflow-visible bg-white shadow-sm">
            <Table className="overflow-visible">
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[50px]">No.</TableHead>
                        <TableHead>Brand</TableHead>
                        <TableHead>Model</TableHead>
                        <TableHead>Item Description</TableHead>
                        <TableHead>Item Code / Part #</TableHead>
                        <TableHead>Serial Number</TableHead>
                        <TableHead>Available Qty.</TableHead>
                        <TableHead>Order Qty.</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                                No items added to order. Use the controls on the left to add items.
                            </TableCell>
                        </TableRow>
                    ) : (
                        rows.map((row, index) => (
                            <TableRow key={row.id} className="hover:bg-gray-50">
                                <TableCell className="font-medium">{index + 1}</TableCell>
                                <TableCell>{row.brand}</TableCell>
                                <TableCell>{row.model}</TableCell>
                                <TableCell className="max-w-[150px] truncate" title={row.description}>
                                    {row.description}
                                </TableCell>
                                <TableCell>{row.lot === 'Spare Part' ? row.partNumber : row.itemCode}</TableCell>
                                <TableCell>
                                    {(row.lot === 'Machine' || row.lot === 'Demo Unit') ? (
                                        <div className="flex items-center gap-1 relative serial-dropdown-container">
                                            <Popover open={openSerialDropdown === row.id} onOpenChange={(open) => setOpenSerialDropdown(open ? row.id : null)}>
                                                <PopoverTrigger asChild>
                                                    <div className="relative w-full flex items-center">
                                                        <input
                                                            type="text"
                                                            value={row.serialNumber}
                                                            onChange={(e) => handleSerialChange(row.id, e.target.value)}
                                                            onKeyDown={(e) => handleSerialKeyDown(e, row.id, e.target.value)}
                                                            onBlur={(e) => handleSerialSubmit(row.id, e.target.value)}
                                                            placeholder="Serial..."
                                                            className="w-full border rounded pl-1 pr-6 py-0.5 text-xs"
                                                        />
                                                        <button
                                                            type="button"
                                                            onMouseDown={(e) => {
                                                                e.preventDefault(); // prevent input blur
                                                                setOpenSerialDropdown(openSerialDropdown === row.id ? null : row.id);
                                                            }}
                                                            className="absolute right-1 text-gray-500 hover:text-gray-700"
                                                            title="Select Serial"
                                                        >
                                                            <ChevronDown size={14} />
                                                        </button>
                                                    </div>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-[200px] p-0 z-[9999]" align="start" sideOffset={4}>
                                                    <div className="max-h-40 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
                                                        {(() => {
                                                            // Get all currently selected serials in other rows for this same lot/item
                                                            const selectedSerials = new Set(
                                                                rows
                                                                    .filter(r => r.id !== row.id && r.serialNumber)
                                                                    .map(r => r.serialNumber)
                                                            );

                                                            const matchingSerials = products
                                                                .filter(p => 
                                                                    p.lot === row.lot && 
                                                                    ((p.itemCode && p.itemCode === row.itemCode) || (p.partNumber && p.partNumber === row.partNumber)) && 
                                                                    p.serialNumber &&
                                                                    !selectedSerials.has(p.serialNumber) // Filter out already selected serials
                                                                )
                                                                .map(p => p.serialNumber);
                                                            
                                                            if (matchingSerials.length === 0) {
                                                                return <div className="px-2 py-1 text-xs text-gray-500">No serials available</div>;
                                                            }
                                                            
                                                            return matchingSerials.map((serial, i) => (
                                                                <div
                                                                    key={i}
                                                                    className="px-2 py-2 hover:bg-gray-100 cursor-pointer text-xs border-b last:border-b-0 font-medium"
                                                                    onMouseDown={(e) => {
                                                                        e.preventDefault();
                                                                        handleSerialChange(row.id, serial);
                                                                        handleSerialSubmit(row.id, serial);
                                                                        setOpenSerialDropdown(null);
                                                                    }}
                                                                >
                                                                    {serial}
                                                                </div>
                                                            ));
                                                        })()}
                                                    </div>
                                                </PopoverContent>
                                            </Popover>
                                            <button
                                                type="button"
                                                onClick={() => handleSerialOpenScanner(row.id)}
                                                className="p-1 border rounded bg-gray-50 hover:bg-gray-100"
                                                title="Scan Serial"
                                            >
                                                <Camera size={14} />
                                            </button>
                                        </div>
                                    ) : (
                                        <span className="text-gray-400 text-xs italic">N/A</span>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {(() => {
                                        // Calculate available qty dynamically
                                        // If Serial is provided, we look for that specific item's quantity (usually 1)
                                        // If Serial is NOT provided, we sum up quantity of all matching Item Codes
                                        
                                        let baseAvailable = 0;
                                        
                                        if (row.serialNumber && row.serialNumber.trim()) {
                                             // Specific serial lookup
                                             const specificItem = products.find(p => p.serialNumber === row.serialNumber.trim());
                                             baseAvailable = specificItem ? (parseInt(specificItem.quantity) || 0) : 0;
                                         } else {
                                            // Generic Item Code lookup (Sum of all batches/units with same Item Code AND same Lot Type)
                                            const normalize = (value) => (value || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
                                            const matchingItems = products.filter(p => {
                                                if (p.lot !== row.lot) return false;
                                                if (row.lot === 'Accessory') {
                                                    return (
                                                        normalize(p.brand) === normalize(row.brand) &&
                                                        normalize(p.model) === normalize(row.model) &&
                                                        normalize(p.description) === normalize(row.description)
                                                    );
                                                }
                                                return (
                                                    (p.itemCode && p.itemCode === row.itemCode) ||
                                                    (p.partNumber && p.partNumber === row.partNumber)
                                                );
                                            });
                                            
                                            // If no matching items found by code, maybe it's a unique item added directly?
                                            if (matchingItems.length > 0) {
                                                 baseAvailable = matchingItems.reduce((sum, p) => sum + (parseInt(p.quantity) || 0), 0);
                                            } else {
                                                 // Fallback to the row's own availableQty if set
                                                 baseAvailable = parseInt(row.availableQty) || 0;
                                            }
                                        }

                                        const orderQty = parseInt(row.orderQuantity.replace(/,/g, ''), 10) || 0;
                                        const finalAvailable = baseAvailable - orderQty;
                                        
                                        return (
                                            <span className={finalAvailable < 0 ? "text-red-500 font-bold" : "text-gray-700"}>
                                                {finalAvailable}
                                            </span>
                                        );
                                    })()}
                                </TableCell>
                                <TableCell>
                                    <input 
                                        type="text" 
                                        value={row.orderQuantity} 
                                        onChange={(e) => handleOrderQtyChange(row.id, e.target.value)}
                                        placeholder="0"
                                        className="w-16 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 text-right font-bold"
                                    />
                                </TableCell>
                                <TableCell className="text-right">
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        onClick={() => handleRemoveRow(row.id)}
                                        className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </div>

        {/* Footer: Submit */}
        <div className="pt-4 border-t mt-4 flex justify-end">
             <Button 
                 onClick={() => setIsSubmitConfirmOpen(true)} 
                 className="bg-[#003366] hover:bg-blue-900 px-8"
             >
                 Submit
             </Button>
         </div>
         <AlertDialog open={isSubmitConfirmOpen} onOpenChange={setIsSubmitConfirmOpen}>
           <AlertDialogContent>
             <AlertDialogHeader>
               <AlertDialogTitle>Submit Stock Out?</AlertDialogTitle>
               <AlertDialogDescription>
                 Are you sure you want to submit this stock out transaction?
               </AlertDialogDescription>
             </AlertDialogHeader>
             <AlertDialogFooter>
               <AlertDialogCancel>Cancel</AlertDialogCancel>
               <AlertDialogAction onClick={handleSubmit}>Confirm</AlertDialogAction>
             </AlertDialogFooter>
           </AlertDialogContent>
         </AlertDialog>
      </div>
    </div>

      <BarcodeScannerModal 
        isOpen={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        onScanSuccess={handleScanSuccess}
      />

      {/* Product Selection Modal */}
      <Dialog open={productSelectionModal.isOpen} onOpenChange={(open) => setProductSelectionModal(prev => ({ ...prev, isOpen: open }))}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                  <DialogTitle>Select Item</DialogTitle>
              </DialogHeader>
              <div className="space-y-2">
                  <p className="text-sm text-gray-500">Multiple items found with this code. Please select one:</p>
                  <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm text-left">
                          <thead className="bg-gray-50 text-gray-700 font-medium border-b">
                              <tr>
                                  <th className="px-4 py-2">Brand</th>
                                  <th className="px-4 py-2">Model</th>
                                  <th className="px-4 py-2">Serial</th>
                                  <th className="px-4 py-2">Qty</th>
                                  <th className="px-4 py-2">Action</th>
                              </tr>
                          </thead>
                          <tbody>
                              {productSelectionModal.items.map((item) => (
                                  <tr key={item.id} className="border-b hover:bg-gray-50">
                                      <td className="px-4 py-2">{item.brand}</td>
                                      <td className="px-4 py-2">{item.model}</td>
                                      <td className="px-4 py-2">{item.serialNumber || '-'}</td>
                                      <td className="px-4 py-2">{item.quantity}</td>
                                      <td className="px-4 py-2">
                                          <button
                                              onClick={() => {
                                                  // Calculate total quantity for all matching items (since we are selecting by item code/model)
                                                  // The modal shows individual units, but selecting one here (without filling serial) means we want the Model.
                                                  const totalQty = productSelectionModal.items.reduce((sum, p) => sum + (parseInt(p.quantity) || 0), 0);
                                                  updateRowWithProduct(productSelectionModal.rowId, item, totalQty, false);
                                                  setProductSelectionModal({ isOpen: false, items: [], rowId: null });
                                              }}
                                              className="text-blue-600 hover:text-blue-800 font-medium"
                                          >
                                              Select
                                          </button>
                                      </td>
                                  </tr>
                              ))}
                          </tbody>
                      </table>
                  </div>
              </div>
          </DialogContent>
      </Dialog>

      {/* Not Found Dialog */}
      <Dialog open={notFoundDialog.isOpen} onOpenChange={(open) => setNotFoundDialog(prev => ({ ...prev, isOpen: open }))}>
          <DialogContent className="sm:max-w-md">
              <DialogHeader>
                  <DialogTitle className="text-red-600">Item Not Found</DialogTitle>
              </DialogHeader>
              <div className="py-4">
                  <p className="text-gray-700">{notFoundDialog.message}</p>
              </div>
              <div className="flex justify-end">
                  <button
                      onClick={() => setNotFoundDialog({ isOpen: false, message: '' })}
                      className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
                  >
                      Close
                  </button>
              </div>
          </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockOut;
